import { PinType, UrlParams } from '@/types';

/**
 * Parse a reveal link. The decryption key travels after "#", which browsers never send to a server, so it
 * stays out of CloudFront's logs and Referer headers:
 *
 *   /secret/<secretIdPart1>/#/<secretIdPart2>/<randomWord>[/pin|/dpin][/signin]
 *
 * `/signin` marks credentials that sign the recipient in through the browser extension: the page must
 * know before opening the link, since a one-time link opened without the extension would be spent.
 *
 * Links made before that change carry everything in the path and still open:
 *
 *   /secret/<secretIdPart1>/<secretIdPart2>/<randomWord>[/pin|/dpin]   (also without /secret)
 *
 * The segments of the path and of the fragment are read as one list, so both shapes parse the same way.
 */
export const parseRevealUrl = (pathname: string, hash = ''): UrlParams | null => {
    try {
        const segments = (value: string) => value.split('/').filter(Boolean);
        let parts = [...segments(pathname), ...segments(hash.replace(/^#/, ''))];

        // Handle /secret/ prefix - remove it if present
        if (parts.length > 0 && parts[0].toLowerCase() === 'secret') {
            parts = parts.slice(1);
        }

        if (parts.length < 3) {
            console.error('[parseRevealUrl] Invalid URL: insufficient path segments. Need 3, got:', parts.length);
            return null;
        }

        const secretIdPart1 = decodeURIComponent(parts[0]);
        const secretIdPart2 = decodeURIComponent(parts[1]);
        const randomWord = decodeURIComponent(parts[2]);
        const secretId = secretIdPart1 + secretIdPart2;

        let pinType: PinType | undefined;

        if (parts.length >= 4) {
            const pinPart = parts[3].toLowerCase();
            if (pinPart === 'pin') {
                pinType = 'static';
            } else if (pinPart === 'dpin') {
                pinType = 'dynamic';
            }
        }

        const signIn = parts.slice(3).some((part) => part.toLowerCase() === 'signin');

        return {
            secretId,
            randomWord,
            ...(pinType ? { pinType } : {}),
            ...(signIn ? { signIn } : {}),
            secretIdPart1,
            secretIdPart2,
        };
    } catch (error) {
        console.error('[parseRevealUrl] Error parsing URL_:', error);
        return null;
    }
};

/**
 * Validate URL parameters
 */
export const validateUrlParams = (params: UrlParams | null): boolean => {
    if (!params) return false;

    const { secretId, randomWord } = params;

    if (!secretId || secretId.length < 10) {
        console.error('Invalid secretId');
        return false;
    }

    if (!randomWord || randomWord.length < 10) {
        console.error('Invalid randomWord (decryption key)');
        return false;
    }

    return true;
};

/**
 * Build a reveal link in the current format, with the second half of the id, the key and the PIN flag after
 * "#" (see parseRevealUrl).
 */
export const buildRevealUrl = (
    baseUrl: string,
    secretIdPart1: string,
    secretIdPart2: string,
    randomWord: string,
    pinType?: PinType,
    includeSecretPrefix = true
): string => {
    const prefix = includeSecretPrefix ? '/secret' : '';
    let url = `${baseUrl}${prefix}/${encodeURIComponent(secretIdPart1)}/#/${encodeURIComponent(secretIdPart2)}/${encodeURIComponent(randomWord)}`;

    if (pinType === 'static') {
        url += '/pin';
    } else if (pinType === 'dynamic') {
        url += '/dpin';
    }

    return url;
};

/**
 * Where the reveal page's "Find it in your portal" takes the secret's owner: the portal's secrets list with
 * that secret's details open (SharePass-Frontend-V2, `?sid=`).
 *
 * Only the id goes, never the key: the portal reads the details as their owner, which neither opens the
 * secret nor counts as a view, and the key would be one more place for it to leak (the portal's address,
 * its history, its logs). Built here because the second half of the id travels after "#", which the
 * server never sees.
 *
 * The user's list, not the admin's: this page cannot know which the owner is, a non-administrator would be
 * turned away from /admin, and the portal moves an administrator to their own list.
 *
 * `portalUrl` is the instance's own (VITE_PORTAL_URL), so a SharePass One page points at its own portal.
 * Without an id it is the portal as before.
 */
export const PORTAL_SECRETS_LIST_PATH = '/secrets-list';
export const PORTAL_SECRET_PARAM = 'sid';

export const buildPortalSecretUrl = (portalUrl: string, secretId?: string | null): string => {
    const base = portalUrl.trim().replace(/\/+$/, '');
    if (!base || !secretId) return base;
    return `${base}${PORTAL_SECRETS_LIST_PATH}?${PORTAL_SECRET_PARAM}=${encodeURIComponent(secretId)}`;
};
